import { useCallback, useEffect } from 'react'
import { AlertTriangle, CalendarX } from 'lucide-react'
import { useGetBookingAlerts } from '../../hooks/backend/interviews'
import { cn } from '../../lib/shadcn/utils'

// One row of interview_booking_alerts_v1 (migration 7600).
export type BookingAlert = {
  alert_key: string
  kind: 'not_linked' | 'booked_twice' | 'old_link' | 'refused_not_cancelled' | 'same_time'
  kind_label: string
  person_name: string
  booked_email: string | null
  applied_email: string | null
  starts_at: string | null
  round: number | null
  assigned_name: string | null
  calendar_of: string | null
  detail: string
  what_to_do: string
  application_id: string | null
}

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-ZA', {
        timeZone: 'Africa/Johannesburg',
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'No time in the booking'

// A booking the interviewer would otherwise only see in Google Calendar. Each
// card says what is wrong with it and what clears it, because the person
// reading it is the one who has to make the call or cancel the booking.
export function BookingAlerts() {
  const { data, error, trigger } = useGetBookingAlerts()

  const load = useCallback(() => {
    trigger({ scope: 'mine' }, { skipCache: true })
  }, [trigger])

  useEffect(() => {
    load()
  }, [load])

  // An array arrives as an array since 23 August, but a failed or empty read
  // must never blank the interviews under it.
  const rows = Array.isArray(data) ? (data as BookingAlert[]) : []
  if (error || rows.length === 0) return null

  return (
    <div className="space-y-3">
      <div>
        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          Calendly bookings that need you ({rows.length})
        </h3>
        <p className="text-sm text-muted-foreground">
          These are on a Calendly calendar but are not cleanly in the cockpit. Each one says why and what clears it.
        </p>
      </div>
      {rows.map((r) => (
        <div
          key={r.alert_key}
          className={cn(
            'rounded-lg border p-3 text-sm',
            r.kind === 'booked_twice' || r.kind === 'refused_not_cancelled'
              ? 'border-red-300 bg-red-50 text-red-950 dark:border-red-900 dark:bg-red-950 dark:text-red-100'
              : 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100',
          )}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-semibold">
              {r.person_name}
              {r.round ? `, Interview ${r.round}` : ''}
            </span>
            <span className="flex items-center gap-1 text-xs font-medium">
              <CalendarX className="h-3.5 w-3.5" />
              {r.kind_label}
            </span>
          </div>
          <p className="mt-1">{when(r.starts_at)}</p>
          <p className="mt-1">{r.detail}.</p>
          <p className="mt-1 text-xs opacity-80">
            {[
              r.booked_email ? `Booked as ${r.booked_email}` : null,
              r.applied_email && r.applied_email !== r.booked_email ? `applied as ${r.applied_email}` : null,
              r.calendar_of ? `on the calendar of ${r.calendar_of}` : null,
            ]
              .filter(Boolean)
              .join(', ')}
          </p>
          <p className="mt-2 font-medium">{r.what_to_do}.</p>
          {r.application_id && (
            <a
              className="mt-1 inline-block text-xs font-medium underline"
              href={`/pipeline?tab=interview&app=${r.application_id}`}
            >
              Open the application
            </a>
          )}
        </div>
      ))}
    </div>
  )
}
